import { ArrowDropdown, Close } from '@/components/icons'
import { duprCountries } from '@/constants/Countries'
import { Country } from '@/types'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'usehooks-ts'
import { ButtonPrimary } from '../button'

type Props = {
  open: boolean
  onClose: () => void
  isoCode?: string
  dialCode?: string
  onSubmit: (value: Country) => void
}

export const SelectCountryModal = ({ open, onClose, isoCode, dialCode, onSubmit }: Props) => {
  const { t } = useTranslation()

  const [selected, setSelected] = useState(duprCountries[0])
  const [query, setQuery] = useState('')

  const ref = useRef<HTMLInputElement>(null)
  const { width } = useWindowSize()

  const maxWidth = width - 172

  const filteredPeople =
    query === ''
      ? duprCountries
      : duprCountries.filter((country: Country) =>
          country.name.toLowerCase().replace(/\s+/g, '').includes(query?.toLowerCase().replace(/\s+/g, ''))
        )

  useEffect(() => {
    if (isoCode && dialCode) {
      const findCountry = duprCountries.find((item) => item.dialCode === dialCode && item?.isoCode === isoCode)
      if (findCountry) setSelected(findCountry)
    }
  }, [isoCode, dialCode, open])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1001]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onClose}
                >
                  <Close className="text-navy-900" />
                </button>
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('common.selectCountry')}
                </Dialog.Title>

                <div className="relative mt-8">
                  <Combobox value={selected} onChange={setSelected}>
                    <Combobox.Button
                      className="flex items-center w-full justify-between border border-navy-900 border-opacity-20 rounded-full p-3"
                      onClick={() => {
                        ref.current?.select()
                      }}
                    >
                      <div className="flex items-center gap-x-2 flex-1">
                        {selected && <p>{selected?.emoji}</p>}
                        <Combobox.Input
                          ref={ref}
                          className="w-full focus:outline-none rounded-full pl-1 text-navy-900 text-sm pr-4"
                          displayValue={(country: Country) => country.name}
                          onChange={(event) => setQuery(event.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-x-1">
                        {selected && <p className="text-sm font-medium text-navy-900">{selected?.dialCode}</p>}
                        <ArrowDropdown />
                      </div>
                    </Combobox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setQuery('')}
                    >
                      <Combobox.Options
                        className={`absolute w-full border-t border-navy-900 border-opacity-20 rounded-lg bg-white z-20 mt-1 cursor-pointer shadow-md max-h-80 overflow-auto`}
                      >
                        {query ? (
                          filteredPeople.length === 0 ? (
                            <div className="relative cursor-default select-none p-2 text-navy-900 text-opacity-50 text-sm font-medium">
                              Nothing found.
                            </div>
                          ) : (
                            filteredPeople.map((country) => (
                              <Combobox.Option
                                className={({ active }) =>
                                  `flex items-center justify-between p-2 overflow-x-hidden ${
                                    active ? 'text-white bg-primary-300' : ''
                                  }`
                                }
                                key={`${country.dialCode + country.isoCode}`}
                                value={country}
                              >
                                <div className="flex items-center gap-x-2">
                                  <p>{country?.emoji}</p>
                                  <p
                                    className={`truncate`}
                                    style={{ maxWidth: width > 540 ? '360px' : `${maxWidth}px` }}
                                  >
                                    {country?.name}
                                  </p>
                                </div>
                                <p className="text-xs font-medium text-slate-500">{country?.dialCode}</p>
                              </Combobox.Option>
                            ))
                          )
                        ) : (
                          duprCountries.map((country) => (
                            <Combobox.Option
                              className={({ active }) =>
                                `flex items-center justify-between p-2 overflow-x-hidden ${
                                  active ? 'text-white bg-primary-300' : ''
                                }`
                              }
                              key={`${country.dialCode + country.isoCode}`}
                              value={country}
                            >
                              <div className="flex items-center gap-x-2">
                                <p>{country?.emoji}</p>
                                <p className={`truncate`} style={{ maxWidth: width > 540 ? '360px' : `${maxWidth}px` }}>
                                  {country?.name}
                                </p>
                              </div>
                              <p className="text-xs font-medium text-slate-500">{country?.dialCode}</p>
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>
                </div>

                <div className="flex items-center justify-center mt-4">
                  <ButtonPrimary
                    title={t('common.submit')}
                    className={`flex items-center justify-center !rounded`}
                    onClick={() => {
                      onSubmit(selected)
                      onClose()
                    }}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
